import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import LaboReport from '@/parcours/flows/labo/LaboReport.vue';
import { api } from '@/services/api';
import { getVrfVisit } from '@/services/labo-vrf';

const MONTH_NAMES = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

function formatPatientBirthDate(patient: any): string {
  const { birth_day, birth_month, birth_year } = patient ?? {};
  if (!birth_day || !birth_month || !birth_year) return 'Non précisé';
  return `${birth_day} ${MONTH_NAMES[Number(birth_month) - 1]} ${birth_year}`;
}

export const laboFlow = withGlobalFlow({
  id: 'laboFlow',

  basePath: '/laboFlow/flow',

  exitRoute: '/laboFlow',

  // Cahier Réf 19 : recharger l'identité visite/patient à CHAQUE ouverture depuis
  // la base (api-v2), au lieu de servir le snapshot localStorage (visitId périmé).
  refetchOnEnter: true,

  async onInit({ route }) {
    const slotId = route.params.slotId;

    const slotRes = await api.get(`/cms/slot/${slotId}`);
    const bookedPatient = slotRes.data?.patients?.[0];

    // Identité de la visite courante, fraîche depuis api-v2 (plus d'api_v1).
    // Renvoie aussi le patient du dossier (via visit.case_id) : indispensable
    // pour un créneau généré depuis un case (EMPTY, aucune réservation) où
    // slot.patients[0] est absent.
    const visitRes = await getVrfVisit(slotId);
    const { patient: casePatient, ...visitInfo } = visitRes.data ?? {};

    return {
      // Priorité au patient réservé sur le créneau ; sinon celui du dossier.
      patient: bookedPatient ?? casePatient ?? null,
      ...visitInfo, // { id (visitId), name, step }
    };
  },

  steps: {
    patientInfos: {
      type: 'component',
      component: ListInfo,
      title: 'Informations patient',
      props: (data: any) => {
        const patient = data?.flowData?.patient;
        const civility = patient?.civility === 'F' ? 'Madame' : 'Monsieur';
        return {
          data,
          config: [
            {
              section: 'Coordonnées du patient',
              items: [
                { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
                {
                  key: 'patient.birth_name',
                  label: 'Nom de naissance',
                  source: 'flowData',
                  showIf: (d: any) => !!d?.patient?.birth_name,
                },
                { key: 'patient.firstname', label: 'Prénom', source: 'flowData' },
                { key: 'patient.civility', label: 'Civilité', value: civility },
                { label: 'Date de naissance', value: formatPatientBirthDate(patient) },
                { key: 'patient.email', label: 'Adresse e-mail', source: 'flowData' },
                { key: 'patient.phone', label: 'N° de téléphone', source: 'flowData' },
              ],
            },
          ],
        };
      },
    },

    laboReport: {
      type: 'component',
      component: LaboReport,
      header: 'internal',
      isButtonNextDisabled: true,
      props: (data: any) => ({ data }),
    },

    // Fin dédiée (plutôt que le message générique `end`) : la signature ne se
    // fait plus dans l'app (pad retiré) mais via Yousign, APRÈS la revue/
    // validation IDEC — l'IDEL doit savoir qu'un mail suivra, pas s'attendre
    // à signer tout de suite.
    laboEnd: {
      type: 'end',
      title: 'Compte-rendu envoyé',
      description:
        'Votre compte-rendu va être relu par l’IDEC. S’il nécessite votre signature, vous recevrez un e-mail Yousign pour signer électroniquement.',
    },
  },

  transitions: {
    patientInfos: { next: 'laboReport' },
    laboReport: { submit: true, success: 'laboEnd' },
  },
});
